import {
	Component,
	h,
	Element,
	Method,
	Host,
	Event,
	Prop,
	EventEmitter,
} from '@stencil/core';
import { closestPolyfill } from '../../../common/_polyfills/closest';
import { TimerFactory } from '../../../common/_helpers/timer';
import isChromatic from 'chromatic/isChromatic';
import {
	moveInlineStylesToHead,
	removeInlineStylesFromHead,
} from '../_helpers/content-security-policy';

const CssTransitionDurationMs = 350;

@Component({
	tag: 'ds-accordion-item',
	shadow: false,
})
export class AccordionItem {
	@Element() el: HTMLDsAccordionItemElement;

	openPane: HTMLElement;
	openInputEl: HTMLInputElement;
	closePane: HTMLElement;
	closeInputEl: HTMLInputElement;
	contentWrapperEl: HTMLElement;
	contentEl: HTMLElement;

	@Event() accordionExpanded: EventEmitter<number>;
	@Event() accordionCollapsed: EventEmitter<number>;
	@Event() accordionKeydown: EventEmitter<{
		index: number;
		originalEvent: KeyboardEvent;
	}>;

	@Prop({ reflect: true, mutable: true }) expanded = false;
	@Prop({ reflect: true }) index: number;

	breakLineHeight = 2;

	heightValueOffset = this.breakLineHeight;
	componentWillLoad(): void {
		closestPolyfill();

		this.openPane = Array.from(this.el.children).find((el) =>
			el.classList.contains('ds-open-pane')
		) as HTMLElement;
		this.openInputEl = Array.from(this.el.children).find((el) =>
			el.classList.contains('accordion-pane')
		) as HTMLInputElement;

		this.closePane = Array.from(this.el.children).find((el) =>
			el.classList.contains('ds-close-pane')
		) as HTMLElement;
		if (this.closePane) {
			this.closeInputEl = this.closePane.querySelector('input');
		}

		this.contentWrapperEl = Array.from(this.el.children).find((el) =>
			el.classList.contains('ds-content-wrapper')
		) as HTMLElement;
		if (this.contentWrapperEl) {
			this.contentEl = this.contentWrapperEl.querySelector(
				'.ds-accordion-content'
			);
			this.contentWrapperEl.style.display = 'none';
		}
	}

	componentDidLoad(): void {
		if (this.openInputEl) {
			if (this.openInputEl.checked) {
				this.expanded = true;
			}
			this.cancelInitialNoJsStyle();
			setTimeout(() => {
				moveInlineStylesToHead(this.el.id);
			}, CssTransitionDurationMs + 100);
		}
	}

	componentDidUpdate() {
		setTimeout(() => {
			moveInlineStylesToHead(this.el.id);
		}, CssTransitionDurationMs + 100);
	}

	disconnectedCallback() {
		removeInlineStylesFromHead(this.el.id);
	}

	handleKeyDown = (event: KeyboardEvent): void => {
		if (
			event.key === 'Enter' ||
			event.key === 'Space' ||
			event.code === 'Space'
		) {
			event.preventDefault();
			(event.target as HTMLElement).click();
		}

		this.accordionKeydown.emit({
			index: this.index,
			originalEvent: event,
		});
	};

	@Method()
	async focusOnLabel() {
		if (this.expanded === true && this.closePane) {
			(
				this.closePane.querySelector('[aria-expanded=true]') as HTMLElement
			).focus();
		} else if (this.openPane) {
			(
				this.openPane.querySelector('[aria-expanded=false]') as HTMLElement
			).focus();
		}
		return;
	}

	@Method()
	async expand() {
		this.expanded = true;
		this.closeInputEl.checked = false;
		this.openInputEl.checked = true;
		this.closeInputEl.setAttribute('checked', '');
		this.openInputEl.setAttribute('checked', 'checked');
		this.setContentWrapperDisplay(true);
		this.setContentWrapperHeight(true);
		this.setContentWrapperOpacity(true);
		this.setParentContentWrapperHeight();
		return;
	}

	@Method()
	async collapse() {
		this.expanded = false;
		this.openInputEl.checked = false;
		this.closeInputEl.checked = true;
		this.openInputEl.setAttribute('checked', '');
		this.closeInputEl.setAttribute('checked', 'checked');
		this.setContentWrapperHeight(false);
		this.setContentWrapperOpacity(false);
		this.setParentContentWrapperHeight();
		this.setContentWrapperDisplay(false);
		return;
	}

	@Method()
	async isExpanded() {
		return this.expanded;
	}

	@Method()
	async setContentWrapperHeight(visible = false) {
		if (this.contentWrapperEl && this.contentEl) {
			if (!isChromatic()) {
				if (visible) {
					const height = this.contentEl.clientHeight + this.heightValueOffset;
					this.contentWrapperEl.style.minHeight = `${height}px`;
					// wait for the transition to finish, then set to auto to allow video transcript to expand the content height.
					setTimeout(() => {
						this.contentWrapperEl.style.height = 'auto';
					}, CssTransitionDurationMs);
				} else {
					this.contentWrapperEl.style.height = '0';
					this.contentWrapperEl.style.minHeight = '0';
				}
			}
		}
	}

	setParentContentWrapperHeight() {
		const parent = this.el.parentElement.closest('ds-accordion-item');
		if (parent) {
			TimerFactory(CssTransitionDurationMs)
				.tickTock()
				.then(() => parent.setContentWrapperHeight(true));
		}
	}

	/**
	 * Set the display to 'block' or 'none' in order to prevent any focusable elements inside the item from being
	 * focusable while it is collapsed.
	 * @param visible
	 */
	setContentWrapperDisplay(visible = false) {
		if (visible) {
			this.contentWrapperEl.style.display = 'block';
		} else {
			TimerFactory(CssTransitionDurationMs)
				.tickTock()
				.then(() => (this.contentWrapperEl.style.display = 'none'));
		}

		return;
	}

	@Method()
	async setContentWrapperOpacity(visible = false) {
		this.contentWrapperEl.style.opacity = visible ? '1' : '0';

		return;
	}

	handleClick = (e: MouseEvent): void => {
		if (
			!!e.target &&
			(e.target as HTMLElement).classList.contains('ds-pane-label')
		) {
			// call preventDefault/stopPropagation only when clicking on the accordion control
			// putting this outside the if statement will stop link inside the accordion item to open (bug ISE-1117)
			e.preventDefault();
			e.stopPropagation();

			if (this.expanded === true) {
				this.collapse().then(() => this.accordionCollapsed.emit(this.index));
			} else {
				this.expand().then(() => this.accordionExpanded.emit(this.index));
			}

			this.focusOnLabel();
		}
	};

	cancelInitialNoJsStyle = (): void => {
		this.setContentWrapperHeight();
		['padding', 'opacity'].forEach((style) => {
			this.contentWrapperEl.style[style] = '0';
		});
	};

	render() {
		return (
			<Host onKeyDown={this.handleKeyDown} onClick={this.handleClick}>
				<slot />
			</Host>
		);
	}
}
